import {
  filterInterface,
  Option,
} from 'Interfaces/FDPInterfaces/constrainedForecastBulkEdit';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { factScreenServices } from 'services/factsScreenServices';
import { constrainedForecastService } from 'services/fdp/constrainedForecastService';
import { tmsScreenServices } from 'services/tmsScreenServices';
import { deepCopy } from 'utils/deepCopy';

export const useFilterSection = ({
  filterpayload,
  handleApplyFilters,
}: {
  filterpayload: filterInterface;
  handleApplyFilters: (filterData: filterInterface) => void;
}) => {
  const RampingOptions: { label: string; value: boolean }[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];
  const StratCodeOptions: { label: string; value: string }[] = [
    { label: 'Model', value: 'model' },
    { label: 'Manual', value: 'manual' },
  ];
  const SellingCategoryOptions: { label: string; value: string }[] = [
    { label: 'Direct', value: 'Direct' },
    { label: 'Indirect', value: 'Indirect' },
  ];

  const [isAllMoleculesLoading, setIsAllMoleculesLoading] = useState<boolean>(true);
  const [isAllConceptLoading, setIsAllConceptLoading] = useState<boolean>(true);
  const [allMolecules, setAllMolecules] = useState<string[]>([]);
  const [allConcepts, setAllConcepts] = useState<string[]>([]);
  const [filteredMolecules, setFilteredMolecules] = useState<string[]>([]);
  const [filteredConcept, setFilteredConcept] = useState<string[]>([]);
  const [filter, setFilter] = useState<filterInterface>({
    Ramping: '',
    Concept: '',
    MoleculeAndDosageForm: '',
    StrategyCode: '',
    SellingCategory: '',
    NDC11: '',
    ConceptSort: '', // Add missing ConceptSort property
  });

  const handleRestFilter = () => {
    setFilter({
      Ramping: '',
      Concept: '',
      MoleculeAndDosageForm: '',
      StrategyCode: '',
      SellingCategory: '',
      NDC11: '',
      ConceptSort: '', // Add missing ConceptSort property
    });
  };

  const handlerFunctions = {
    handleFilterMolecules: async () => {
      const tempMolecules: string[] = [];

      for (const item of allMolecules) {
        if (item.toLowerCase().includes(filter.MoleculeAndDosageForm.toLowerCase())) {
          tempMolecules.push(item);
        }
      }
      if (tempMolecules.length === 0) {
        tempMolecules.push('No matching items found');
      }
      setFilteredMolecules(tempMolecules);
    },

    handleFilterConcepts: async () => {
      const tempConcepts: string[] = [];

      for (const item of allConcepts) {
        if (item.toLocaleLowerCase().search(filter.Concept.toLocaleLowerCase()) !== -1) {
          tempConcepts.push(item);
        }
      }
      if (tempConcepts.length === 0) {
        tempConcepts.push('No matching items found');
      }

      setFilteredConcept(tempConcepts);
    },
  };

  const abortControllerRef = useRef<AbortController | null>(null);

  const getfilterOption = {
    getAllMolecules: async () => {
      try {
        setIsAllMoleculesLoading(true);
        const molecules = await factScreenServices.getAllMoleculeDetails();
        const temp = molecules.map(item => item.molecule_dose_form);
        setAllMolecules(temp);
      } catch (_error) {
        toast.error('Cannot fetch all the molecules');
      } finally {
        setIsAllMoleculesLoading(false);
      }
    },

    getAllConcepts: async () => {
      try {
        setIsAllConceptLoading(true);
        const concepts = await tmsScreenServices.getAllGSMSConceptForSalesPlan();
        const temp = concepts.map(item => item.concept);
        setAllConcepts(temp);
      } catch (_error) {
        toast.error('Cannot fetch all the concepts');
      } finally {
        setIsAllConceptLoading(false);
      }
    },

    fetchNDCSuggestions: async (inputValue: string): Promise<Option[]> => {
      // cancel any ongoing request if there is one
      if (abortControllerRef.current) {
        abortControllerRef.current.abort(); // Abort the previous requrest
      }

      // Create a new Abort controller for the new request
      const controller = new AbortController();
      abortControllerRef.current = controller;

      return new Promise(resolve => {
        setTimeout(async () => {
          try {
            const response = await constrainedForecastService.getNDC11Suggestions(
              inputValue,
              controller.signal
            );
            const suggestions: Option[] = [];

            for (const item of response) {
              suggestions.push({ label: item, value: item });
            }

            resolve(suggestions);
          } catch (_error) {
            console.log('Error fetching the suggestions: ');
            resolve([]);
          }
        }, 500);
      });
    },
  };

  // Cleanup on component unmount (cancel any ongoing request)
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    // getting all the molecules
    getfilterOption.getAllMolecules();
    getfilterOption.getAllConcepts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilterValues = (field: keyof filterInterface, value: string) => {
    const filterCopy = deepCopy(filter);
    filterCopy[field] = value;
    setFilter(filterCopy);
  };

  const handleFilter = () => {
    if (JSON.stringify(filter) === JSON.stringify(filterpayload)) {
      return;
    }
    handleApplyFilters(filter);
  };

  return {
    RampingOptions,
    StratCodeOptions,
    SellingCategoryOptions,
    isAllMoleculesLoading,
    filter,
    setFilter,
    filteredMolecules,
    isAllConceptLoading,
    filteredConcept,
    setFilterValues,
    handleFilter,
    handlerFunctions,
    getfilterOption,
    handleRestFilter,
  };
};
